/**
 * Draft storage utilities using localStorage
 */

const DRAFT_PREFIX = "zettl_draft_";

export function saveDraft<T>(key: string, data: T): void {
  try {
    const draftKey = `${DRAFT_PREFIX}${key}`;
    const draftData = {
      data,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(draftKey, JSON.stringify(draftData));
  } catch (error) {
    console.error("Failed to save draft:", error);
  }
}

export function loadDraft<T>(key: string): T | null {
  try {
    const draftKey = `${DRAFT_PREFIX}${key}`;
    const stored = localStorage.getItem(draftKey);
    if (!stored) return null;

    const draftData = JSON.parse(stored);
    return draftData.data as T;
  } catch (error) {
    console.error("Failed to load draft:", error);
    return null;
  }
}

export function getDraftInfo(key: string): { savedAt: string } | null {
  try {
    const draftKey = `${DRAFT_PREFIX}${key}`;
    const stored = localStorage.getItem(draftKey);
    if (!stored) return null;

    const draftData = JSON.parse(stored);
    return { savedAt: draftData.savedAt };
  } catch (error) {
    return null;
  }
}

export function clearDraft(key: string): void {
  try {
    const draftKey = `${DRAFT_PREFIX}${key}`;
    localStorage.removeItem(draftKey);
  } catch (error) {
    console.error("Failed to clear draft:", error);
  }
}

export function hasDraft(key: string): boolean {
  const draftKey = `${DRAFT_PREFIX}${key}`;
  return localStorage.getItem(draftKey) !== null;
}

// Draft keys
export const DRAFT_KEYS = {
  CLIENT_NEW: "client_new",
  CLIENT_EDIT: (id: string) => `client_edit_${id}`,
  INVOICE_NEW: "invoice_new",
  INVOICE_EDIT: (id: string) => `invoice_edit_${id}`,
} as const;

/**
 * Removes every saved draft. Drafts are keyed without the user (a new-client
 * draft is "zettl_draft_client_new"), so on a shared browser the next person
 * to sign in would get the previous user's half-typed client details.
 */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to clear drafts:", error);
  }
}
